<script setup lang="ts">
import LineInfoCircle from '@docue/docue-ui-v2/icons/LineInfoCircle.vue'

import type { SubscriptionPaymentPeriodUnit } from '~/types'
import type { SubscriptionAddon } from '~/composables/subscriptionProcess'
import type { PriceListAddon } from '~/composables/api/price-list/useFetchPriceList'

withDefaults(defineProps<{
  addon: PriceListAddon
  periodUnit: SubscriptionPaymentPeriodUnit
  disabled?: boolean
}>(), {  })

const modelValue = defineModel<SubscriptionAddon[]>({ required: true })

const { t } = useI18n()
const { formatCurrency } = useIntl()
const { translateForCountry } = useCountryAwareTranslation()

const addonPrice = computed(() => {
  const price = __props.addon.prices.find(({ period_unit }) => period_unit === __props.periodUnit)?.price
  if (!price)
    return undefined

  return __props.periodUnit === 'month' ? price / 100 : price / 100 / 12
})

const formattedPrice = computed(() => addonPrice.value ? formatCurrency(addonPrice.value, { maximumFractionDigits: 0, maximumSignificantDigits: 2 }) : undefined)

const isBilledPerUnit = computed(() => __props.addon.prices.find(({ period_unit }) => period_unit === __props.periodUnit)?.pricing_model === 'per_unit')
const isAddonSelected = computed(() => modelValue.value.some(({ addonId }) => addonId === __props.addon.addon_id))
const isQuantityEditable = computed(() => isBilledPerUnit.value && isAddonSelected.value)

const quantity = ref(modelValue.value.find(({ addonId }) => addonId === __props.addon.addon_id)?.quantity ?? 1)
const quantityThrottled = refThrottled(quantity, 1000)

watch(quantityThrottled, (value) => {
  modelValue.value = modelValue.value.map(({ addonId, quantity }) => addonId === __props.addon.addon_id ? { addonId, quantity: value } : { addonId, quantity })
})

const handleAddonUpdate = (value: boolean | undefined) => {
  if (value) {
    modelValue.value.push({ addonId: __props.addon.addon_id, quantity: 1 })
  }
  else {
    modelValue.value = modelValue.value.filter(({ addonId }) => addonId !== __props.addon.addon_id)
  }
}

const label = computed(() => {
  switch (__props.addon.addon_id) {
    case 'workspace-viewer-pt':
      return {
        content: t('subscriptionAddonSelectorItem.addons.viewer-license.title'),
        tooltip: t('subscriptionAddonSelectorItem.addons.viewer-license.description'),
      }
    case 'extra-users-pt':
      return {
        content: t('subscriptionAddonSelectorItem.addons.user-license.title'),
        tooltip: t('subscriptionAddonSelectorItem.addons.user-license.description'),
      }
    case 'unlimited-contracts-pt':
      return {
        content: t('subscriptionAddonSelectorItem.addons.unlimited-contracts.title'),
        tooltip: t('subscriptionAddonSelectorItem.addons.unlimited-contracts.description'),
      }
    case 'unlimited-template-access-pt':
      return {
        content: translateForCountry('subscribe.subscriptionAddonSelectorItem.addons.unlimited-template-access.title'),
        tooltip: translateForCountry('subscribe.subscriptionAddonSelectorItem.addons.unlimited-template-access.description'),
      }
    case 'unlimited-e-signing-pt':
      return {
        content: t('subscriptionAddonSelectorItem.addons.unlimited-e-signing.title'),
        tooltip: translateForCountry('subscribe.subscriptionAddonSelectorItem.addons.unlimited-e-signing.description'),
      }
    default:
      return undefined
  }
})

const entity = computed(() => {
  switch (__props.addon.addon_id) {
    case 'workspace-viewer-pt':
      return t('subscriptionAddonSelectorItem.addons.viewer-license.entity')
    case 'extra-users-pt':
      return t('subscriptionAddonSelectorItem.addons.user-license.entity')
    default:
      return undefined
  }
})

const description = computed(() => {
  if (!formattedPrice.value)
    return undefined

  const parts = [formattedPrice.value, entity.value].filter(Boolean).join('/')

  return `+${parts}/${t('month')}`
})
</script>

<template>
  <div class="flex items-start justify-between gap-2">
    <DTCheck
      :model-value="isAddonSelected"
      :description="description"
      :disabled="disabled"
      @update:model-value="handleAddonUpdate"
    >
      <template #label>
        <div class="inline-flex w-max shrink-0 select-none items-center gap-1">
          {{ label?.content }}
          <DTTooltip
            v-if="label?.tooltip"
            :content="label.tooltip"
            variant="dark"
          >
            <span class="shrink-0">
              <LineInfoCircle class="size-4" />
            </span>
          </DTTooltip>
        </div>
      </template>
    </DTCheck>

    <BaseNumberInputOffsettable
      v-if="isQuantityEditable"
      v-model="quantity"
      :min="1"
      :max="100"
      :disabled="disabled"
    />
  </div>
</template>
